import {
  format,
  isToday,
  isThisWeek,
  isThisMonth,
  isBefore,
  parseISO,
} from "date-fns";
import { ar } from "date-fns/locale";

/**
 * Utility Functions for Maintenance Schedules
 */

// ========== Date Formatting ==========

/**
 * تنسيق التاريخ بشكل قابل للقراءة
 */
export const formatDate = (date, formatStr = "yyyy-MM-dd") => {
  try {
    const parsedDate = typeof date === "string" ? parseISO(date) : date;
    return format(parsedDate, formatStr, { locale: ar });
  } catch (error) {
    console.error("Error formatting date:", error);
    return date;
  }
};

/**
 * تنسيق التاريخ والوقت معاً
 */
export const formatDateTime = (date) => {
  return formatDate(date, "yyyy-MM-dd HH:mm");
};

/**
 * تنسيق التاريخ بالعربي
 */
export const formatDateArabic = (date) => {
  return formatDate(date, "dd MMMM yyyy");
};

/**
 * التحقق من أن التاريخ متأخر
 */
export const isOverdue = (scheduleDate, status) => {
  try {
    const date =
      typeof scheduleDate === "string" ? parseISO(scheduleDate) : scheduleDate;
    const today = new Date();
    return isBefore(date, today) && status !== "completed";
  } catch (error) {
    console.error("Error checking overdue:", error);
    return false;
  }
};

/**
 * فلترة الجداول حسب التاريخ
 */
export const filterByDate = (schedules, dateFilter) => {
  if (dateFilter === "all") return schedules;

  return schedules.filter((schedule) => {
    try {
      const scheduleDate = parseISO(schedule.schedule_date);

      switch (dateFilter) {
        case "today":
          return isToday(scheduleDate);
        case "week":
          return isThisWeek(scheduleDate);
        case "month":
          return isThisMonth(scheduleDate);
        case "overdue":
          return isOverdue(scheduleDate, schedule.status);
        default:
          return true;
      }
    } catch (error) {
      console.error("Error filtering by date:", error);
      return false;
    }
  });
};

// ========== Status Management ==========

/**
 * الحصول على النص العربي للحالة
 */
export const getStatusText = (status) => {
  const statusMap = {
    pending: "قيد الانتظار",
    in_progress: "جاري التنفيذ",
    completed: "مكتمل",
    cancelled: "ملغي",
    on_hold: "معلق",
  };
  return statusMap[status] || status;
};

/**
 * الحصول على الـ class للحالة
 */
export const getStatusClass = (status) => {
  const statusClassMap = {
    pending: "status-pending",
    in_progress: "status-in-progress",
    completed: "status-completed",
    cancelled: "status-cancelled",
    on_hold: "status-on-hold",
  };
  return statusClassMap[status] || "";
};

/**
 * الحصول على اللون للحالة
 */
export const getStatusColor = (status) => {
  const statusColorMap = {
    pending: "#ffc107",
    in_progress: "#17a2b8",
    completed: "#28a745",
    cancelled: "#dc3545",
    on_hold: "#6c757d",
  };
  return statusColorMap[status] || "#6c757d";
};

// ========== Priority Management ==========

/**
 * الحصول على النص العربي للأولوية
 */
export const getPriorityText = (priority) => {
  const priorityMap = {
    low: "منخفض",
    medium: "متوسط",
    high: "عالي",
    urgent: "عاجل",
  };
  return priorityMap[priority] || priority;
};

/**
 * الحصول على الـ class للأولوية
 */
export const getPriorityClass = (priority) => {
  const priorityClassMap = {
    low: "priority-low",
    medium: "priority-medium",
    high: "priority-high",
    urgent: "priority-urgent",
  };
  return priorityClassMap[priority] || "";
};

/**
 * الحصول على اللون للأولوية
 */
export const getPriorityColor = (priority) => {
  const priorityColorMap = {
    low: "#6c757d",
    medium: "#17a2b8",
    high: "#fd7e14",
    urgent: "#dc3545",
  };
  return priorityColorMap[priority] || "#6c757d";
};

// ========== Data Export ==========

/**
 * تحضير البيانات للتصدير إلى Excel
 */
export const prepareDataForExport = (schedules) => {
  return schedules.map((schedule) => ({
    "عنوان الجدول": schedule.title || "-",
    "تاريخ الصيانة": formatDate(schedule.schedule_date),
    "وقت البداية": schedule.start_time || "-",
    "وقت النهاية": schedule.end_time || "-",
    الحالة: getStatusText(schedule.status),
    الأولوية: getPriorityText(schedule.priority),
    "يحتوي على طفايات": schedule.has_fire_extinguishers ? "نعم" : "لا",
    "الفني المكلف": schedule.assignedTechnician?.name || "غير محدد",
    "مدير الصيانة": schedule.maintenanceManager?.name || "غير محدد",
    "اسم الشركة":
      schedule.contractSchedule?.branch?.company?.name || "غير محدد",
    الفرع: schedule.contractSchedule?.branch?.name || "غير محدد",
    المدينة: schedule.contractSchedule?.branch?.city || "غير محدد",
    "رقم العقد": schedule.contractSchedule?.contract_number || "غير محدد",
    "قيمة العقد": schedule.contractSchedule?.value || "غير محدد",
    "عدد العناصر": schedule.scheduleItems?.length || 0,
    "تاريخ التوكيل": schedule.assignment_date
      ? formatDate(schedule.assignment_date)
      : "غير محدد",
    "تاريخ الإنشاء": formatDateTime(schedule.created_at),
    "آخر تحديث": formatDateTime(schedule.updated_at),
    ملاحظات: schedule.notes || "-",
  }));
};

// ========== Data Validation ==========

/**
 * التحقق من صحة بيانات الجدول
 */
export const validateSchedule = (schedule) => {
  const errors = [];

  if (!schedule.title || schedule.title.trim() === "") {
    errors.push("عنوان الجدول مطلوب");
  }

  if (!schedule.schedule_date) {
    errors.push("تاريخ الصيانة مطلوب");
  }

  if (!schedule.start_time) {
    errors.push("وقت البداية مطلوب");
  }

  if (!schedule.end_time) {
    errors.push("وقت النهاية مطلوب");
  }

  if (
    schedule.start_time &&
    schedule.end_time &&
    schedule.start_time >= schedule.end_time
  ) {
    errors.push("وقت النهاية يجب أن يكون بعد وقت البداية");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// ========== Statistics ==========

/**
 * حساب إحصائيات الجداول
 */
export const calculateStatistics = (schedules) => {
  const stats = {
    total: schedules.length,
    pending: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
    overdue: 0,
    withFireExtinguishers: 0,
    highPriority: 0,
  };

  schedules.forEach((schedule) => {
    // حساب الحالات
    if (schedule.status === "pending") stats.pending++;
    else if (schedule.status === "in_progress") stats.inProgress++;
    else if (schedule.status === "completed") stats.completed++;
    else if (schedule.status === "cancelled") stats.cancelled++;

    // حساب المتأخرة
    if (isOverdue(schedule.schedule_date, schedule.status)) {
      stats.overdue++;
    }

    // حساب الطفايات
    if (schedule.has_fire_extinguishers) {
      stats.withFireExtinguishers++;
    }

    // حساب الأولوية العالية
    if (schedule.priority === "high" || schedule.priority === "urgent") {
      stats.highPriority++;
    }
  });

  return stats;
};

/**
 * حساب نسبة الإنجاز
 */
export const calculateCompletionRate = (schedules) => {
  if (schedules.length === 0) return 0;

  const completed = schedules.filter((s) => s.status === "completed").length;
  return Math.round((completed / schedules.length) * 100);
};

// ========== Filtering ==========

/**
 * فلترة متقدمة للجداول
 */
export const advancedFilter = (schedules, filters) => {
  let filtered = [...schedules];

  // فلتر التاريخ
  if (filters.dateFilter && filters.dateFilter !== "all") {
    filtered = filterByDate(filtered, filters.dateFilter);
  }

  // فلتر الحالة
  if (filters.statusFilter && filters.statusFilter !== "all") {
    filtered = filtered.filter((s) => s.status === filters.statusFilter);
  }

  // فلتر الأولوية
  if (filters.priorityFilter && filters.priorityFilter !== "all") {
    filtered = filtered.filter((s) => s.priority === filters.priorityFilter);
  }

  // فلتر الطفايات
  if (
    filters.fireExtinguishersFilter !== undefined &&
    filters.fireExtinguishersFilter !== "all"
  ) {
    filtered = filtered.filter(
      (s) => s.has_fire_extinguishers === filters.fireExtinguishersFilter
    );
  }

  // فلتر البحث
  if (filters.searchTerm && filters.searchTerm.trim() !== "") {
    const searchLower = filters.searchTerm.toLowerCase();
    filtered = filtered.filter(
      (s) =>
        s.title?.toLowerCase().includes(searchLower) ||
        s.assignedTechnician?.name?.toLowerCase().includes(searchLower) ||
        s.maintenanceManager?.name?.toLowerCase().includes(searchLower) ||
        s.contractSchedule?.branch?.company?.name
          ?.toLowerCase()
          .includes(searchLower)
    );
  }

  return filtered;
};

// ========== Sorting ==========

/**
 * ترتيب الجداول
 */
export const sortSchedules = (schedules, sortBy, sortOrder = "asc") => {
  const sorted = [...schedules];

  sorted.sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case "date":
        aValue = new Date(a.schedule_date);
        bValue = new Date(b.schedule_date);
        break;
      case "title":
        aValue = a.title || "";
        bValue = b.title || "";
        break;
      case "status":
        aValue = a.status || "";
        bValue = b.status || "";
        break;
      case "priority":
        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
        aValue = priorityOrder[a.priority] || 0;
        bValue = priorityOrder[b.priority] || 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
};

// ========== Local Storage ==========

/**
 * حفظ الفلاتر في localStorage
 */
export const saveFiltersToLocalStorage = (filters) => {
  try {
    localStorage.setItem("maintenanceScheduleFilters", JSON.stringify(filters));
  } catch (error) {
    console.error("Error saving filters to localStorage:", error);
  }
};

/**
 * استرجاع الفلاتر من localStorage
 */
export const loadFiltersFromLocalStorage = () => {
  try {
    const saved = localStorage.getItem("maintenanceScheduleFilters");
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error("Error loading filters from localStorage:", error);
    return null;
  }
};

export default {
  formatDate,
  formatDateTime,
  formatDateArabic,
  isOverdue,
  filterByDate,
  getStatusText,
  getStatusClass,
  getStatusColor,
  getPriorityText,
  getPriorityClass,
  getPriorityColor,
  prepareDataForExport,
  validateSchedule,
  calculateStatistics,
  calculateCompletionRate,
  advancedFilter,
  sortSchedules,
  saveFiltersToLocalStorage,
  loadFiltersFromLocalStorage,
};
